/** @license
 *
 * Copyright 2021 Esri
 *
 * All rights reserved under the copyright laws of the United States and applicable international laws, treaties,
 * and conventions.
 *
 * This material is licensed for use under the Esri Master License Agreement (MLA), and is bound by the terms of
 * that agreement. You may redistribute and use this code without modification, provided you adhere to the terms
 * of the MLA and include this copyright notice.
 *
 * See use restrictions at http://www.esri.com/legal/pdfs/mla_e204_e300/english
 *
 * For additional information, contact: Environmental Systems Research Institute, Inc. Attn: Contracts and Legal
 * Services Department 380 New York Street Redlands, California, USA 92373 USA
 *
 * email: contracts@esri.com
 */

/**
 * This is a subset of the esri.com gn.js menu-building utility.
 *
 * @module gn
 */

/**
 * Builds flyout-style menu items.
 *
 * @param menuNode JSON node containing flyout menu descriptions
 * @return List of configuration info for flyout menu items
 */
export function buildFlyoutMenu(
  menuNode: any
): any {
  const flyoutMenus: any[] = [];
  for (let y = 0; y < parseInt(menuNode.flyoutCount as string, 10); y++) {
    if (menuNode['flyout-menu-' + y]) {
      const flyoutMenuNode = menuNode['flyout-menu-' + y];
      const flyoutInfo = {
        category: flyoutMenuNode.flyoutTitle,
        cols: [] as any[]
      };
      let colCount = 1;
      if (flyoutMenuNode.hasSecondColumn === 'true') {
        colCount = 2;
      }
      for (let z = 1; z <= colCount; z++) {
        const flyoutCols = flyoutMenuNode['flyout-col-' + z];
        const flyoutCol = [] as any[];

        for (const key in flyoutCols) {
          if (key !== 'jcr:primaryType') {
            const linkNode = flyoutCols[key];
            flyoutCol.push({
              heading: linkNode.heading,
              label: linkNode.label,
              href: linkNode.href
            });
          }
        }
        flyoutInfo.cols.push({
          col: flyoutCol
        });
      }
      flyoutMenus.push(flyoutInfo);
    }
  }
  return flyoutMenus;
}

/**
 * Generates the JSON for building the global-nav footer section 3 menus: the assorted links to Esri legal and other resources.
 *
 * @param footerConfig Section jcr:content.root.global_footer from the international global-nav configuration
 * @return List of configuration info for the menuData.footer.info.menu section of the global-nav configuration
 */
export function buildFooterInfoMenu(
  footerConfig: any
): any {
  const results = getChildEntries(footerConfig.infolinks).map(function(entry) {
    return {
      label: entry.linklabel,
      href: entry.linkurl
    };
  });

  return results;
}

/**
 * Generates the JSON for building the global-nav footer section 2 menus: the assorted links to Esri resources.
 *
 * @param footerConfig Section jcr:content.root.global_footer from the international global-nav configuration
 * @return List of configuration info for the menuData.footer.menu.menu section of the global-nav configuration
 */
export function buildFooterMenu(
  footerConfig: any
): any[] {
  const results: any[] = [];
  for (let i = 1; i <= 5; i++) {
    const titleProp = 'menu' + i + 'title';
    const linksProp = 'menu' + i + 'links';
    const footerMenu = {
      label: footerConfig[titleProp],
      menu: getChildEntries(footerConfig[linksProp]).map(function(entry) {
        return {
          label: entry.linklabel,
          href: entry.linkurl
        };
      })
    };

    if (footerMenu.label && footerMenu.menu && footerMenu.menu.length > 0) {
      results.push(footerMenu);
    }
  }
  return results;
}

/**
 * Generates the JSON for building the global-nav footer section 1 menus: the assorted links to social media.
 *
 * @return List of configuration info for the menuData.footer.social.menu section of the global-nav configuration
 */
export function buildFooterSocialMenu(
): any[] {
  return [{
    label: "Facebook",
    href: "https://www.facebook.com/esrigis",
    image: {
      viewBox: "0 0 38 38",
      path: ["M38 38V0H0v38h17.2V21.9H14v-5.7h3.2v-3.7c0-2.6 1.2-6.7 6.7-6.7h4.9v5.5h-3.6c-.6 0-1.4.3-1.4 1.5v3.3h5.1l-.6 5.7h-4.5v16.1H38z"]
    }
  }, {
    label: "Twitter",
     href: "https://twitter.com/Esri",
    image: {
      viewBox: "0 0 512 512",
      path: ["M512.002 97.211c-18.84 8.354-39.082 14.001-60.33 16.54 21.686-13 38.342-33.585 46.186-58.115a210.29 210.29 0 0 1-66.705 25.49c-19.16-20.415-46.461-33.17-76.674-33.17-58.011 0-105.042 47.029-105.042 105.039 0 8.233.929 16.25 2.72 23.939-87.3-4.382-164.701-46.2-216.509-109.753-9.042 15.514-14.223 33.558-14.223 52.809 0 36.444 18.544 68.596 46.73 87.433a104.614 104.614 0 0 1-47.577-13.139c-.01.438-.01.878-.01 1.321 0 50.894 36.209 93.348 84.261 103a105.245 105.245 0 0 1-27.674 3.687c-6.769 0-13.349-.66-19.764-1.888 13.368 41.73 52.16 72.104 98.126 72.949-35.95 28.176-81.243 44.967-130.458 44.967-8.479 0-16.84-.496-25.058-1.471 46.486 29.807 101.701 47.197 161.021 47.197 193.211 0 298.868-160.062 298.868-298.872 0-4.554-.104-9.084-.305-13.59 20.526-14.809 38.335-33.309 52.417-54.373z"]
    }
  }, {
    label: "LinkedIn",
     href: "https://www.linkedin.com/company/esri",
    image: {
      viewBox: "0 0 24 24",
      path: ["M0 0v24h24V0zm8 19H5V8h3zM6.5 6.7A1.8 1.8 0 1 1 8.3 5a1.8 1.8 0 0 1-1.8 1.7zM20 19h-3v-5.6c0-3.4-4-3.1-4 0V19h-3V8h3v1.8c1.4-2.6 7-2.8 7 2.5z"]
    }
  }, {
    label: "GeoNet",
     href: "https://community.esri.com/",
    image: {
      viewBox: "0 0 32 32",
      path: "./assets/images/esricommunity-30.svg"
    }
  }, {
    label: "Instagram",
     href: "https://www.instagram.com/esrigram/",
    image: {
      viewBox: "0 0 30 30",
      path: ["M29.91 8.815c-.073-1.596-.327-2.686-.697-3.64a7.354 7.354 0 0 0-1.73-2.657 7.352 7.352 0 0 0-2.657-1.73C23.87.416 22.78.162 21.184.09 19.584.017 19.074 0 15 0s-4.585.017-6.184.09C7.219.163 6.129.417 5.174.787a7.352 7.352 0 0 0-2.656 1.73 7.354 7.354 0 0 0-1.73 2.657C.416 6.13.162 7.22.09 8.815.017 10.415 0 10.926 0 15s.017 4.585.09 6.184c.073 1.597.327 2.687.697 3.642a7.353 7.353 0 0 0 1.73 2.656 7.353 7.353 0 0 0 2.657 1.73c.955.371 2.045.625 3.642.698 1.6.073 2.11.09 6.184.09s4.585-.017 6.184-.09c1.597-.073 2.687-.327 3.642-.697a7.353 7.353 0 0 0 2.656-1.73 7.353 7.353 0 0 0 1.73-2.657c.371-.955.625-2.045.698-3.642.073-1.6.09-2.11.09-6.184s-.017-4.585-.09-6.185zm-2.997 12.232c-.064 1.412-.287 2.153-.496 2.691a4.376 4.376 0 0 1-1.056 1.623 4.371 4.371 0 0 1-1.622 1.055c-.539.21-1.28.433-2.691.497-1.573.072-2.045.087-6.048.087s-4.475-.015-6.047-.087c-1.412-.064-2.153-.287-2.691-.496a4.376 4.376 0 0 1-1.623-1.056 4.371 4.371 0 0 1-1.055-1.622c-.21-.539-.433-1.28-.497-2.691C3.015 19.475 3 19.003 3 15s.015-4.475.087-6.047c.064-1.412.287-2.153.496-2.691a4.38 4.38 0 0 1 1.056-1.623A4.371 4.371 0 0 1 6.26 3.584c.539-.21 1.28-.433 2.691-.497C10.525 3.015 10.997 3 15 3s4.475.015 6.047.087c1.412.064 2.153.287 2.691.496a4.38 4.38 0 0 1 1.623 1.056c.493.493.8.963 1.055 1.622.21.539.433 1.28.497 2.691.072 1.573.087 2.045.087 6.048s-.015 4.475-.087 6.047zM15 7.175a7.825 7.825 0 1 0 0 15.65 7.825 7.825 0 0 0 0-15.65zm0 12.65c-2.66 0-4.825-2.164-4.825-4.825s2.164-4.825 4.825-4.825 4.825 2.164 4.825 4.825-2.164 4.825-4.825 4.825zm6.007-12.832a2 2 0 1 0 4 0 2 2 0 1 0-4 0"]
    }
  }, {
    label: "YouTube",
     href: "https://www.youtube.com/user/esritv/",
    image: {
      viewBox: "0 0 310 310",
      path: ["M297.917 64.645c-11.19-13.302-31.85-18.728-71.306-18.728H83.386c-40.359 0-61.369 5.776-72.517 19.938C0 79.663 0 100.008 0 128.166v53.669c0 54.551 12.896 82.248 83.386 82.248h143.226c34.216 0 53.176-4.788 65.442-16.527C304.633 235.518 310 215.863 310 181.835v-53.669c0-29.695-.841-50.16-12.083-63.521zm-98.896 97.765l-65.038 33.991a9.997 9.997 0 0 1-14.632-8.863v-67.764a10 10 0 0 1 14.609-8.874l65.038 33.772a10 10 0 0 1 .023 17.738z"]
    }
  }];
}

/**
 * Generates the JSON for building the global-nav header menus.
 *
 * @param headerConfig Section jcr:content.root.global_navigation_v2 from the international global-nav configuration
 * @return List of configuration info for the menuData.header.menus section of the global-nav configuration
 */
export function buildHeaderMenus(
  headerConfig: any
): any[][] {
  const results: any[] = [];
  // Loop over all menus
  for (let i = 0; i < parseInt(headerConfig.numberofmenus as string, 10); i++) {
    const menuNode = headerConfig['menu-' + i] || {};
    const menuInfo = {
      label: menuNode.menulabel,
      menuType: menuNode.menuType,
      flyout: [] as any[],
      cols: [] as any[],
      tiles: [] as any[]
    };

    switch (menuInfo.menuType) {
      case 'flyout':
        menuInfo.flyout = buildFlyoutMenu(menuNode);
        break;
      case 'columnBased':
      default:
        menuInfo.cols = buildStructuredCols(menuNode);
        menuInfo.tiles = buildTiles(menuNode);
        break;
    }

    results.push(menuInfo);
  }
  return [results];
}

/**
 * Builds column-style menu items.
 *
 * @param menuNode JSON node containing column menu descriptions
 * @return List of configuration info for column menu items
 */
export function buildStructuredCols(
  menuNode: any
): any[] {
  const menuCols: any[] = [];
  for (let y = 0; y < parseInt(menuNode.columnCount as string, 10); y++) {
    const columnNode = menuNode['column-' + y];
    const linksNode = columnNode[columnNode.columnType + 'Links'];

    const colInfo = {
      type: columnNode.columnType,
      border: columnNode.rightBorder === "true",
      items: getChildEntries(linksNode).map(function(link) {
        return {
          heading: link.heading,
          label: link.label,
          href: link.url,
          description: link.description
        };
      })
    };
    menuCols.push(colInfo);
  }
  return menuCols;
}

/**
 * Builds tile-style menu items.
 *
 * @param menuNode JSON node containing tile menu descriptions
 * @return List of configuration info for tile menu items
 */
export function buildTiles(
  menuNode: any
): any[] {
  let menuTiles: any[] = [];

  const tilesNode = menuNode.menutiles;
  if (tilesNode) {
    menuTiles = getChildEntries(tilesNode).map(function(tile) {
      return {
        label: tile.tilelabel,
        href: tile.tileurl,
        icon: tile.tileicon
      };
    });
  }

  return menuTiles;
}

/**
 * Extracts the individual menus from part of the from the international global-nav configuration.
 *
 * @param childProp JSON node from whom child menus are to be extracted
 * @return List of configuration info for child menus
 */
export function getChildEntries(
  childProp: any
): any[] {
  const results: any[] = [];
  for (const key in childProp) {
    if (key !== 'jcr:primaryType') {
      if (childProp.hasOwnProperty(key)) {
        results.push(childProp[key]);
      }
    }
  }
  return results;
}
